import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function PeterboroughCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Peterborough Pothole Claims | 29% of U-Roads RED at Peak | Fixtyer"
        description="Peterborough City Council's own data shows 29% of unclassified roads in RED condition at peak, with only 25% surveyed each year. All four DfT 2025/26 scorecards are AMBER. See what this means for your pothole damage claim."
        canonical="/councils/peterborough"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Peterborough', url: '/councils/peterborough' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'All four DfT scorecards are AMBER — what does that mean for my claim?',
            answer: 'Peterborough earns AMBER on overall performance, condition, spend and best practice for 2025/26. That does not guarantee compensation, but it means the government has not rated any pillar of the council\'s highway maintenance as meeting top-tier benchmarks. Your claim still turns on Section 58 — whether the specific defect that damaged your vehicle was reasonably inspected and repaired — but you are not facing a council with GREEN scorecards across the board.',
          },
          {
            question: 'What if my pothole was on a residential cul-de-sac or rural lane?',
            answer: 'Unclassified roads make up 550km — 59% of Peterborough\'s 931.4km network. The council inspects only around 25% of U-roads each year using Course Visual Inspection (CVI), meaning any given side street may go four years between condition surveys. At peak, 29% of U-roads were in RED condition in both 2022 and 2023 — roughly 160km of residential and rural routes.',
          },
          {
            question: 'U-road RED condition fell from 29% to 22% — does that weaken my claim?',
            answer: 'Not necessarily. The 22% figure is from 2024 survey data and the council itself states U-road condition "has been deteriorating" before noting the recent improvement. CVI covers only a quarter of the network annually, so year-on-year percentages reflect different road samples as much as genuine recovery. If your incident was in 2022 or 2023, the documented peak of 29% RED is the relevant published benchmark.',
          },
          {
            question: 'Does the ended corporate borrowing programme matter?',
            answer: 'Yes, as context. Peterborough acknowledges that corporate borrowing previously funded resurfacing on northern and eastern routes and Parkway sections, helping maintain condition percentages — but states this funding "is not sustainable in the long term and so is not currently available." That is an admission that recent condition stability was partly financed by borrowing the council can no longer deploy.',
          },
          {
            question: 'Does the climate change and alluvium soils admission help my claim?',
            answer: 'Potentially, if your incident was on Peterborough\'s eastern road network. The council states those roads lie on alluvium soils that expand and contract significantly across seasons, with damage frequency and extent increasing — "largely due to climate change." That documents elevated deterioration risk on a defined part of the network, which is relevant when assessing whether inspection intervals were reasonable.',
          },
          {
            question: 'Peterborough filled only 3,453 potholes in 2024/25 — are the roads fixed?',
            answer: 'No. The 3,453 figure is a drop from 5,252 in 2023/24, but the council still estimates around 3,500 pothole repairs for 2025/26. Across five years it filled 17,294 potholes on a 931km network while carrying out preventative maintenance on just over 15km of carriageway in 2024/25 alone. Reactive repairs continue at scale.',
          },
          {
            question: 'How do I report a pothole to Peterborough City Council?',
            answer: 'Use the council\'s online reporting system at report.peterborough.gov.uk, selecting the pothole category. Reporting creates a dated council record — useful evidence whether or not you pursue a claim, especially if the defect was reported before your incident and left unrepaired.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Peterborough City Council"
        councilSlug="peterborough"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/peterborough"
        headline="Peterborough Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Peterborough</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Peterborough: 29% of Side Streets RED — One Quarter Surveyed a Year
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Peterborough earns <strong className="text-amber-400">AMBER on all four DfT scorecards</strong> for
            2025/26 — overall, condition, spend and best practice. Its own transparency data shows{' '}
            <strong className="text-rose-400">29% of unclassified roads in RED condition at peak</strong> across
            550km of cul-de-sacs and rural lanes, while the council inspects only{' '}
            <strong className="text-amber-400">around 25% of that network each year</strong> — even as it filled{' '}
            <strong className="text-rose-400">5,252 potholes in 2023/24 alone</strong>.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">29%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition at peak (2022–2023)</div>
            <div className="text-brand-300 text-sm mt-1">
              On 550km of unclassified roads — roughly 160km of residential streets and rural lanes in the
              worst category, surveyed via driven inspection on a rolling 25% sample each year.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and CVI survey data from Peterborough City Council&apos;s own transparency
            report — main roads stable, side streets under strain
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (122km — 13% of network): consistently strong</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">1%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">flat at 1% every year since 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">11%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 15% in 2023</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">88%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 84% in 2023</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              SCANNER surveys A-roads annually, including the slow lane on dual carriageways each year.
              Main routes are in good shape — but they are a small slice of the network motorists use daily.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (259.4km — 28% of network): broadly stable, C-roads edging up</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">B roads — Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">C roads — Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">C roads — Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">4%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">5%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-700">71%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              The council states B and C road condition "has maintained a constant condition" thanks to
              maintenance works. C-road RED share has ticked back up to 5% in 2024, with green-condition
              C-roads falling from 75% to 71% since 2022.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And the DfT still rates condition AMBER</h3>
            <p className="text-brand-200 mb-4">
              Despite stable A-roads and broadly flat B-roads, the Department for Transport&apos;s 2025/26
              condition scorecard for Peterborough is <strong className="text-amber-400">AMBER</strong> — not
              GREEN. The government&apos;s rating reflects the wider picture, including unclassified road
              deterioration that the council&apos;s own narrative acknowledges.
            </p>
            <p className="text-xl font-semibold text-white">
              Good main-road data does not cancel out{' '}
              <span className="text-rose-400">nearly 160km of RED U-roads at peak</span> on a network where
              most motorists live, park and commute.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road blind spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 550km blind spot
          </h2>
          <p className="text-surface-600 mb-8">
            59% of Peterborough&apos;s network is unclassified roads — and only a quarter is condition-surveyed each year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Survey year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">26%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">29%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">29%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">22%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 25% rolling sample</h3>
              <p className="text-surface-600 text-sm mb-3">
                Peterborough uses Course Visual Inspection (CVI) — a driven survey — for U-roads. Because
                of the length of the unclassified network, the council inspects around{' '}
                <strong>25% of U-roads each year</strong> to produce the condition score. That implies a
                four-year cycle to cover the full estate, not annual network-wide knowledge.
              </p>
              <p className="text-surface-600 text-sm">
                The network consists of less-trafficked rural roads and cul-de-sacs across the city. If your
                pothole was on a residential street, ask whether that road was in the surveyed quarter for
                the year of your incident.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Deterioration, then a partial recovery</h3>
              <p className="text-amber-800 text-sm mb-3">
                The council states U-road condition "has been deteriorating" before noting that 2024 survey
                results show an improvement to 22% RED. Even so, 22% still means roughly{' '}
                <strong>121km of U-roads in the worst category</strong> — and the rolling survey means the
                headline percentage may not reflect the specific street where you were damaged.
              </p>
              <p className="text-amber-800 text-sm">
                From 2025/26, Peterborough plans to extend SCANNER surveys to unclassified roads — a
                methodology change that will alter how condition is reported, similar to the national shift
                to PAS 2161 from 2026/27.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Peterborough&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the 25% of U-roads surveyed in the year of your incident?</li>
              <li>• If 29% of U-roads were RED at peak, what was done about defects on your street?</li>
              <li>• How does a four-year rolling sample provide timely knowledge of potholes forming between inspections?</li>
              <li>• Does CVI driven inspection at road speed catch defects of the size and depth that damaged your vehicle?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge when it samples only a quarter of its
              residential roads each year — on routes where nearly one in three was RED at peak.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            17,294 potholes in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from Peterborough&apos;s own transparency report — including a spike in 2023/24
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Financial year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,603</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,681</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,305</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">5,252</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">3,453</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17,294</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~9 potholes a day, every day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Peterborough fills around nine potholes daily on a 931.4km network.
                The council anticipates around 3,500 repairs for 2025/26. A network producing defects at this
                rate is one where potholes routinely form between inspections — the scenario where prior
                reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">15km of preventative work in 2024/25</h3>
              <p className="text-amber-800 text-sm">
                Against 17,294 reactive pothole fills over five years, the council carried out preventative
                maintenance on just over <strong>15km of carriageway in 2024/25</strong> — under 2% of the
                total road network. For 2025/26 it plans around 22km of carriageway improvement. Reactive
                volume outpaces structural renewal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Spending: above allocation, but rated AMBER
          </h2>
          <p className="text-surface-600 mb-8">
            Peterborough often spends beyond its DfT capital allocation — yet earns an AMBER spend scorecard for 2025/26
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT capital allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative share</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">£8,570,094</td>
                  <td className="px-4 py-3 text-center">£9,289,980</td>
                  <td className="px-4 py-3 text-center">71%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£6,285,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£11,241,063</td>
                  <td className="px-4 py-3 text-center">75%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£7,107,526</td>
                  <td className="px-4 py-3 text-center">£6,608,815</td>
                  <td className="px-4 py-3 text-center">65%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£5,740,000</td>
                  <td className="px-4 py-3 text-center">£6,799,837</td>
                  <td className="px-4 py-3 text-center">65%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£6,240,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£12,097,474</td>
                  <td className="px-4 py-3 text-center">80%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£7,126,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£13,080,919</td>
                  <td className="px-4 py-3 text-center">81%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Previously we have been successful in receiving additional funding through Corporate Borrowing and this has been used to improve the condition of the roads out to the north and east of the city. Corporate Borrowing was also used to fund resurfacing elements of the Parkway network and a programme to improve the safety fencing across the city. This funding is not sustainable in the long term and so is not currently available."
              source="Peterborough City Council Local Highway Maintenance Transparency Report 2025/26"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What the borrowing admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Peterborough used corporate borrowing to fund resurfacing on northern, eastern and Parkway
                routes — and credits it with helping maintain condition percentages. The council now states
                that funding is no longer available.
              </p>
              <p className="text-surface-600 text-sm">
                That is relevant context for claims on roads that benefited from borrowed investment: the
                council knew those routes needed capital intervention, funded them temporarily, and has not
                replaced that revenue stream.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why spend is still AMBER</h3>
              <p className="text-amber-800 text-sm">
                Despite projected 2025/26 capital spend of £9.29m against an £8.57m DfT allocation, the
                Department for Transport rates Peterborough&apos;s spend scorecard <strong>AMBER</strong> — not
                GREEN. Aggregate investment alone has not satisfied the government&apos;s benchmarks, and
                U-road condition deterioration underlines the gap between spend and outcomes on the roads
                most residents use.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Climate change admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The eastern network and alluvium soils
          </h2>
          <p className="text-surface-600 mb-8">
            Peterborough&apos;s own explanation for accelerated deterioration on part of its network
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Within Peterborough the eastern road network lies on alluvium soils. These expand and contract significantly across seasons as they become saturated and then dry out. Recent years have seen the extent and frequency of this cycle, and the level of damage caused increase, presenting an increasing maintenance burden, largely due to climate change."
              source="Peterborough City Council Local Highway Maintenance Transparency Report 2025/26"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council documents that roads on alluvium soils in the east of the city face an
                intensifying damage cycle — and attributes the increased burden to climate change. That is
                documented knowledge of elevated risk on a defined geographic part of the network.
              </p>
              <p className="text-surface-600 text-sm">
                Knowledge of accelerated deterioration raises the standard for what a &quot;reasonable&quot;
                inspection regime looks like on affected routes under Section 58.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your incident on the eastern road network on alluvium soils?</li>
                <li>• Did the council increase inspection frequency on routes it knew were climate-vulnerable?</li>
                <li>• If damage cycles are intensifying, why was the specific defect not caught in time?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER across the board means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an all-AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: no GREEN scorecard cushion — but a well-documented transparency report to work with
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-roads consistently at just 1% RED — SCANNER surveyed annually
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> B-road condition broadly stable at 3–4% RED over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Often spends above DfT capital allocation in recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 65–81% of capital spend classed as preventative maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy aligned to UKRLG code of practice
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on A-roads and main B/C routes. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER on all four DfT scorecards — no pillar rated GREEN
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 29% of U-roads RED at peak across 550km of residential and rural routes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only 25% of U-roads condition-surveyed each year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 17,294 pothole fills in five years — defects form between inspections
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Corporate borrowing that maintained condition is no longer available
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Climate-driven deterioration admitted on eastern alluvium-soil routes
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with no GREEN scorecards, your claim still lives or dies on the{' '}
              <strong className="text-white">specific defect</strong> — but Peterborough&apos;s transparency
              data gives you more angles than many authorities:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council online reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the 25% rolling survey gap is your strongest structural argument</li>
              <li>• Location on the eastern alluvium-soil network if climate-driven deterioration is relevant</li>
              <li>• Timing relative to the 2022/23 peak of 29% RED U-road condition</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Peterborough City Council&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Report a pothole in Peterborough
          </h2>
          <p className="text-surface-600 mb-8">
            Reporting the defect creates a council record — useful evidence whether or not you claim
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Council reporting channels</h3>
              <p className="text-surface-600 text-sm mb-4">
                Peterborough City Council directs residents to its online reporting system for potholes.
                The highway asset management page links directly to a pothole-specific report form, and
                the wider &quot;report it online&quot; portal covers highways faults alongside other council services.
              </p>
              <ul className="text-sm space-y-2">
                <li>
                  <a
                    href="https://report.peterborough.gov.uk/?filter_group=Pothole&filter_category=Pothole"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Report a pothole online
                  </a>
                  <span className="text-surface-500"> — dedicated pothole reporting form</span>
                </li>
                <li>
                  <a
                    href="https://www.peterborough.gov.uk/report-it-online"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Report it online
                  </a>
                  <span className="text-surface-500"> — highways and other council issues</span>
                </li>
                <li>
                  <a
                    href="https://www.peterborough.gov.uk/residents/transport-and-streets/highway-asset-management"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Highway asset management
                  </a>
                  <span className="text-surface-500"> — pothole reporting guidance and transparency data</span>
                </li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why reporting matters for claims</h3>
              <p className="text-amber-800 text-sm mb-3">
                A dated online report shows the council had notice of a defect before your incident — or
                that it left a known pothole unrepaired within a reasonable time. On U-roads where only 25%
                are condition-surveyed annually, resident reports may be the council&apos;s primary source of
                knowledge about a specific street.
              </p>
              <p className="text-amber-800 text-sm">
                Report first, photograph the defect with a reference object for scale, and keep your
                confirmation reference. If you later claim, that record strengthens a Section 41 argument.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Peterborough?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An all-AMBER council still owes a duty of care on the specific defect. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 25% survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No alluvium-soil corridor analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 29% U-road RED peak documented</li>
                <li>✅ 25% rolling survey gap argued</li>
                <li>✅ 17,294 pothole fills in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Peterborough</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=peterborough"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Peterborough claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                All four DfT scorecards are AMBER — what does that mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Peterborough earns AMBER on overall performance, condition, spend and best practice for
                2025/26. That does not guarantee compensation, but it means the government has not rated
                any pillar of the council&apos;s highway maintenance as meeting top-tier benchmarks. Your claim
                still turns on Section 58 — whether the specific defect that damaged your vehicle was
                reasonably inspected and repaired — but you are not facing a council with GREEN scorecards
                across the board.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential cul-de-sac or rural lane?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 550km — 59% of Peterborough&apos;s 931.4km network. The council
                inspects only around 25% of U-roads each year using Course Visual Inspection (CVI), meaning
                any given side street may go four years between condition surveys. At peak, 29% of U-roads
                were in RED condition in both 2022 and 2023 — roughly 160km of residential and rural routes.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                U-road RED condition fell from 29% to 22% — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily. The 22% figure is from 2024 survey data and the council itself states
                U-road condition &quot;has been deteriorating&quot; before noting the recent improvement. CVI covers
                only a quarter of the network annually, so year-on-year percentages reflect different road
                samples as much as genuine recovery. If your incident was in 2022 or 2023, the documented
                peak of 29% RED is the relevant published benchmark.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the ended corporate borrowing programme matter?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes, as context. Peterborough acknowledges that corporate borrowing previously funded
                resurfacing on northern and eastern routes and Parkway sections, helping maintain condition
                percentages — but states this funding &quot;is not sustainable in the long term and so is not
                currently available.&quot; That is an admission that recent condition stability was partly
                financed by borrowing the council can no longer deploy.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the climate change and alluvium soils admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially, if your incident was on Peterborough&apos;s eastern road network. The council states
                those roads lie on alluvium soils that expand and contract significantly across seasons, with
                damage frequency and extent increasing — &quot;largely due to climate change.&quot; That documents
                elevated deterioration risk on a defined part of the network, which is relevant when
                assessing whether inspection intervals were reasonable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Peterborough filled only 3,453 potholes in 2024/25 — are the roads fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. The 3,453 figure is a drop from 5,252 in 2023/24, but the council still estimates
                around 3,500 pothole repairs for 2025/26. Across five years it filled 17,294 potholes on a
                931.4km network while carrying out preventative maintenance on just over 15km of carriageway
                in 2024/25 alone. Reactive repairs continue at scale.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How do I report a pothole to Peterborough City Council?
              </h3>
              <p className="text-surface-600 text-sm">
                Use the council&apos;s online reporting system at report.peterborough.gov.uk, selecting the
                pothole category. Reporting creates a dated council record — useful evidence whether or not
                you pursue a claim, especially if the defect was reported before your incident and left
                unrepaired.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.peterborough.gov.uk/asset-library/annex-a-local-highway-maintenance-transparency-report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Peterborough City Council Local Highway Maintenance Transparency Report 2025/26
            </a>{' '}
            (published via{' '}
            <a
              href="https://www.peterborough.gov.uk/residents/transport-and-streets/highway-asset-management"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Highway Asset Management
            </a>
            , updated June 2026). Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
